import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { AvailableTime } from '../../../api/models/available-time';
import { filter, startWith, Subscription, take } from 'rxjs';
import { CalendarService } from '../../../services/calendar/calendar.service';
import { CalendarDateFirstService } from '../../../services/calendar/date-first/calendar-date-first.service';
import { AvailableCourtTime } from '../../../api/models/available-court-time';
import { DatePickService } from '../../../pages/date-pick/service/date-pick.service';
import { DateHelperService } from '../../../services/date-helper/date-helper.service';
import { FlashMessageErrorHandlerService } from '../../../services/flash-message-error-handler/flash-message-error-handler.service';

@Component({
    selector: 'otb-time-pick-selection',
    templateUrl: './time-pick-selection.component.html',
    styleUrls: ['./time-pick-selection.component.scss', './time-pick-selection.component.mags-sop.scss'],
    standalone: false,
})
export class TimePickSelectionComponent implements AfterViewInit, OnDestroy, OnInit {
    @Input() availableAgencies: Array<AvailableCourtTime> = [];
    @Input() dateFirst: boolean = true;
    @Input() set reservationLoading(loading: boolean | null) {
        this.reservationRunning = loading === true;
        if (!this.reservationRunning) {
            this.submitBtnActive = true;
        }
    }
    @Output() timeSelect = new EventEmitter<AvailableTime>();

    @ViewChildren('slot') slotElements: QueryList<ElementRef<HTMLElement>>;

    subs: Subscription = new Subscription();
    selectedDate: Date = new Date();
    timePickSecondLoading: boolean = false;
    selectedTime?: AvailableTime;
    timeSelected: boolean = false;
    submitBtnActive: boolean = true;
    selectedValuesText: string = '';
    reservationRunning: boolean = false;

    constructor(
        private calendarService: CalendarService,
        private calendarDateFirstService: CalendarDateFirstService,
        private datePickService: DatePickService,
        private dateHelperService: DateHelperService,
        private flashMessageErrorHandlerService: FlashMessageErrorHandlerService,
    ) {}

    ngOnInit(): void {
        this.subs.add(
            this.calendarDateFirstService.timePickSecondLoading.subscribe((loading) => {
                this.timePickSecondLoading = loading;
                if (loading) {
                    this.timeSelected = false;
                    this.selectedValuesText = '';
                }
            }),
        );

        // closing the error alert drops the focus to the document.
        this.subs.add(
            this.flashMessageErrorHandlerService.errorDismissed.subscribe(() => {
                if (this.dateFirst && this.calendarDateFirstService.focusFirstSelectableDay()) {
                    return;
                }

                this.slotElements?.first?.nativeElement.focus();
            }),
        );
    }

    ngAfterViewInit(): void {
        this.subs.add(
            this.calendarService.selectedDate.subscribe((date) => {
                this.selectedDate = date;
            }),
        );

        if (this.dateFirst) {
            this.datePickService.setInitialFocus();

            return;
        }

        this.focusFirstTimeWhenRendered();
    }

    /**
     * In der Zeitansicht wird die Zeitliste nachgeladen, die Uhrzeiten stehen also erst nach
     * diesem Lebenszyklus im DOM.
     */
    private focusFirstTimeWhenRendered(): void {
        const rendered$ = this.slotElements.changes.pipe(
            startWith(this.slotElements),
            filter((slots: QueryList<ElementRef<HTMLElement>>) => slots.length > 0),
            take(1),
        );

        this.subs.add(
            rendered$.subscribe((slots: QueryList<ElementRef<HTMLElement>>) =>
                this.datePickService.setInitialTimeFocus(slots.first),
            ),
        );
    }

    /**
     * Wird von Klick, Enter und Leertaste aufgerufen. Das preventDefault verhindert bei der
     * Leertaste zusätzlich das Scrollen der Seite.
     */
    timeClick(time: AvailableTime, event: Event): void {
        event.preventDefault();

        if (this.dateFirst && this.reservationRunning) {
            return;
        }

        this.selectedTime = time;

        if (this.dateFirst) {
            this.timeSelected = true;
            this.submitBtnActive = true;
            this.selectedValuesText = this.buildSelectedValuesText();
            return;
        }

        this.timeSelect.emit(time);
    }

    submit(): void {
        if (!this.submitBtnActive || !this.timeSelected || !this.selectedTime) {
            return;
        }

        this.submitBtnActive = false;
        this.timeSelect.emit(this.selectedTime);
    }

    resetSelection(): void {
        this.selectedTime = undefined;
        this.timeSelected = false;
        this.selectedValuesText = '';
        this.submitBtnActive = true;
    }

    private buildSelectedValuesText(): string {
        const textArr = [this.dateHelperService.formatBookingDay(this.selectedDate)];
        if (this.selectedTime?.time) {
            textArr.push(this.selectedTime.time);
        }

        return textArr.join(', ');
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}
